import { isNil } from 'lodash'
import {
  flatten,
  includes,
  isEmpty,
  join,
  map,
  range,
  reduce,
  size,
  slice,
  split
} from 'lodash/fp'
import React, { type ReactElement } from 'react'
import {
  Form,
  type Identifier,
  NotFound,
  type RaRecord,
  type RowClickFunction,
  ShowBase,
  required,
  useGetOne,
  useRecordContext,
  useTranslate
} from 'react-admin'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  styled,
  Tab as MuiTab,
  Tabs,
  Typography
} from '@mui/material'
import {
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfMonth,
  subDays
} from 'date-fns/fp'
import {
  DemandChartDaily,
  DemandChartMonthly,
  DemandChartTrend,
  DemandChartYearly,
  ElectricityChartDaily,
  ElectricityChartMonthly,
  ElectricityChartYearly,
  ElectricityMonthlyTable
} from 'components/Chart'
import log from 'loglevel'
import { DateInput, type ScrollProps } from 'components/Util'
import { MultipleSpotSelect, SpotSelect } from 'components/SpotSelect'
import { type ConfigProps } from 'components/Dashboard'
import {
  ElectricityChartDailyKind,
  ElectricityChartMonthlyKind,
  ElectricityChartYearlyKind
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { type DateView } from '@mui/x-date-pickers'
import { type Mapping } from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'
import { useNavigate } from 'react-router-dom'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const Tab = styled((props: any) => <MuiTab {...props} />)(() => ({
  '&.Mui-selected': {
    background: '#F9DD87',
    fontWeight: 'bold'
  }
}))

const SpotShowContent: React.FC<
  {
    partner: string
    date: string
    kind: string | null | undefined
    startMonthOfFiscalYear: string
    setDateViews: (value: DateView[]) => void
  } & ConfigProps &
    ScrollProps
> = ({
  partner,
  date,
  kind: kindArg,
  config,
  scrollRef,
  startMonthOfFiscalYear,
  setDateViews
}) => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const {
    Spot: configSpot,
    Chart: configChart,
    Mappings: configMappings
  } = config
  const { tabs, kinds: configKinds, showAmount, showDemand } = configSpot
  const record = useRecordContext() as Mapping
  const { spin, classification, kind, name, group } = record
  const compareToKinds = [
    [
      ElectricityChartDailyKind.lastWeek,
      ElectricityChartDailyKind.lastYearWithMonthlyAverage,
      ElectricityChartDailyKind.lastYearWithYearlyAverage
    ],
    configKinds.monthly ?? [
      ElectricityChartMonthlyKind.lastYear,
      ElectricityChartMonthlyKind.lastMonth,
      ElectricityChartMonthlyKind.lastYearWithMonthlyAverage
    ],
    configKinds.monthly ?? [
      ElectricityChartMonthlyKind.lastYear,
      ElectricityChartMonthlyKind.lastMonth,
      ElectricityChartMonthlyKind.lastYearWithMonthlyAverage
    ],
    configKinds.yearly ?? [ElectricityChartYearlyKind.lastYear],
    []
  ]
  const dateViews: DateView[][] = [
    ['year', 'month', 'day'],
    ['year', 'month'],
    ['year', 'month'],
    ['year'],
    ['year', 'month']
  ]
  const [tab, setTab] = React.useState<number>(isNil(kindArg) ? 0 : 4)
  const [demandTab, setDemandTab] = React.useState<number>(
    isNil(kindArg) ? 0 : kindArg === 'monthly' ? 1 : 2
  )
  const [compareTo, setCompareTo] = React.useState<
    | ElectricityChartDailyKind
    | ElectricityChartMonthlyKind
    | ElectricityChartMonthlyKind
    | ElectricityChartYearlyKind
    | undefined
  >((compareToKinds[tab] as any[])[0])
  const kinds = [['高圧'], ['低圧'], ['高圧', '低圧']]
  const [showKinds, setShowKinds] = React.useState<string[]>(
    kinds[
      includes('高圧')(kind) ? (includes('低圧')(kind) ? 2 : 0) : 1
    ] as string[]
  )
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setTab(newValue)
    setDateViews(dateViews[newValue] as DateView[])
    setCompareTo(
      (
        compareToKinds[newValue] as Array<
          | ElectricityChartDailyKind
          | ElectricityChartMonthlyKind
          | ElectricityChartYearlyKind
        >
      )[0]
    )
    setShowCompareTo(
      // @ts-expect-error ts(2399)
      reduce.convert({ cap: false })(
        (sum: Record<string, boolean>, target: string, index: number) => ({
          ...sum,
          [target]: index === 0
        }),
        {}
      )(compareToKinds[newValue])
    )
    setTempature([false, false])
    setDemandTab(0)
    navigate({
      pathname: join('/')(slice(0, 4)(split('/')(window.location.pathname)))
    })
  }
  const handleDemandChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setDemandTab(newValue)
    setDateViews((newValue === 2 ? dateViews[0] : dateViews[1]) as DateView[])
    navigate({
      pathname: join('/')(slice(0, 4)(split('/')(window.location.pathname)))
    })
  }
  // let targets: string[] = []
  const { useTarget } = config
  // const { area, office, subs } = configMappings
  // if (isNil(useTarget) || (useTarget as boolean)) {
  //   targets = concat([area, office])(
  //     !isNil(subs) ? map((sub: string) => sub)(subs) : []
  //   )
  // }
  const { organizations } = configMappings
  // if (isNil(useTarget) || (useTarget as boolean)) {
  //   targets = concat([area, office])(
  //     !isNil(subs) ? map((sub: string) => sub)(subs) : []
  //   )
  // }
  // const targetsPlus = concat(targets)(['addressState', 'addressCity'])
  const targetsPlus = flatten(
    map((item: string) => [`${item}（全体）`, `${item}（グループ）`])(
      organizations
    )
  )
  // const targetsPlus = flatten(
  //   map((item: string) => [`${item}（全体）`, `${item}（グループ）`])(targets)
  // )
  const [showCompareTo, setShowCompareTo] = React.useState(
    // @ts-expect-error ts(2399)
    reduce.convert({ cap: false })(
      (sum: Record<string, boolean>, target: string, index: number) => ({
        ...sum,
        [target]: index === 0
      }),
      {}
    )(compareToKinds[tab])
  )
  const [showTargets, setShowTargets] = React.useState(
    isNil(useTarget) || (useTarget as boolean)
      ? reduce(
          (sum: Record<string, boolean>, target: string) => ({
            ...sum,
            // [target]: target === area || target === office
            [target]: includes(target)(organizations)
          }),
          {}
        )(targetsPlus)
      : undefined
  )
  const [others, setOthers] = React.useState(false)
  const [tempature, setTempature] = React.useState<boolean[]>([false, false])
  const [deleted, setDeleted] = React.useState(false)
  const [selected, setSelected] = React.useState<RaRecord[]>([])
  const spotChartControl = (
    mode: string,
    compareToKinds?: string[]
  ): ReactElement =>
    React.useMemo(() => {
      const handleCompareToChange = (
        event: React.ChangeEvent<HTMLInputElement>
      ): void => {
        setShowCompareTo({
          ...showCompareTo,
          [event.target.name]: event.target.checked
        })
      }
      const handleTargetsChange = (
        event: React.ChangeEvent<HTMLInputElement>
      ): void => {
        setShowTargets({
          ...showTargets,
          [event.target.name]: event.target.checked
        })
      }
      const handleOthersChange = (
        event: React.ChangeEvent<HTMLInputElement>
      ): void => {
        setOthers(event.target.checked)
      }
      const handleTempatureChange =
        (index: number) =>
        (event: React.ChangeEvent<HTMLInputElement>): void => {
          const value = JSON.parse(JSON.stringify(tempature))
          value[index] = event.target.checked
          setTempature(value)
        }
      // const handleKindsChange = (
      //   _event: React.SyntheticEvent,
      //   newValue: string
      // ): void => {
      //   setShowKinds(split(',')(newValue))
      // }
      return mode === 'none' ? (
        <></>
      ) : (
        <FormControl sx={{ ml: '0.5em', bgcolor: '#F9DD87', width: '100%' }}>
          <FormGroup row sx={{ ml: '0.5em' }}>
            {/* @ts-expect-error ts(23339) */}
            {map.convert({ cap: false })((kind: string, index: number) => (
              <FormControlLabel
                key={`SpotShowContent.FormControl.FormGroup1.FormControlLabel.${index}`}
                value={kind}
                control={
                  <Checkbox
                    onChange={handleCompareToChange}
                    checked={showCompareTo[kind]}
                    name={kind}
                  />
                }
                label={translate(`resources.spots.${mode}.compareTo.${kind}`)}
                // disabled={size(compareToKinds) === 1}
              />
            ))(compareToKinds)}
            {mode === 'daily' && (
              <FormControlLabel
                key={`SpotShowContent.FormControl.FormGroup1.FormControlLabel.tempature`}
                control={
                  <Checkbox
                    onChange={handleTempatureChange(0)}
                    checked={tempature[0]}
                  />
                }
                label={translate('resources.spots.tempature')}
              />
            )}
            {mode === 'monthly' && (
              <>
                <FormControlLabel
                  key={`SpotShowContent.FormControl.FormGroup1.FormControlLabel.naxTempature`}
                  control={
                    <Checkbox
                      onChange={handleTempatureChange(0)}
                      checked={tempature[0]}
                    />
                  }
                  label={translate('resources.spots.maxTempature')}
                />
                <FormControlLabel
                  key={`SpotShowContent.FormControl.FormGroup1.FormControlLabel.minTempature`}
                  control={
                    <Checkbox
                      onChange={handleTempatureChange(1)}
                      checked={tempature[1]}
                    />
                  }
                  label={translate('resources.spots.minTempature')}
                />
              </>
            )}
            {mode !== 'yearly' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <FormControlLabel
                  key={`SpotShowContent.FormControl.FormGroup1.FormControlLabel.other`}
                  control={
                    <Checkbox onChange={handleOthersChange} checked={others} />
                  }
                  label={translate('resources.spots.others')}
                />
                <MultipleSpotSelect
                  resource="mappings"
                  selected={selected}
                  setSelected={setSelected}
                  deleted={deleted}
                  setDeleted={setDeleted}
                  config={config}
                  opened={false}
                  noAll
                />
              </Box>
            )}
          </FormGroup>
          {mode !== 'yearly' && !isNil(showTargets) && (
            <FormGroup row sx={{ ml: '0.5em' }}>
              {map((index: number) => {
                return (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-start' }}
                    key={index}>
                    {map((item: number) => {
                      const target = targetsPlus[index * 2 + item] as string
                      return (
                        <FormControlLabel
                          key={`SpotShowContent.FormControl.FormGroup2.FormControlLabel.${item}`}
                          control={
                            <Checkbox
                              onChange={handleTargetsChange}
                              checked={showTargets[target]}
                              name={target}
                              disabled={
                                isNil(group) && includes('グループ')(target)
                              }
                            />
                          }
                          label={`${
                            target === 'addressState'
                              ? '都道府県'
                              : target === 'addressCity'
                              ? '市区町村'
                              : target
                          }平均`}
                        />
                      )
                    })(range(0)(2))}
                  </Box>
                )
              })(range(0)(size(targetsPlus) / 2))}
              {/* <FormControlLabel
              control={
                <Checkbox
                  onChange={handleTargetsChange}
                  checked={showTargets}
                />
              }
              label={`${join('、')(targets)}を表示`}
            /> */}
            </FormGroup>
          )}
          {/* {!isNil(kinds) && !isNil(useTarget) && (useTarget as boolean) && (
          <RadioGroup
            row
            value={showKinds}
            onChange={handleKindsChange}
            sx={{ ml: '0.5em' }}>
            {map.convert({ cap: false })((kind: string[], index: number) => {
              return (
                <FormControlLabel
                  key={`SpotShowContent.FormControl.RadioGroup3.FormControlLabel.${index}`}
                  value={join(',')(kind)}
                  control={<Radio />}
                  label={`${
                    includes('高圧')(kind) && includes('低圧')(kind)
                      ? '全電圧'
                      : (kind[0] as string)
                  }平均`}
                />
              )
            })(kinds)}
          </RadioGroup>
        )} */}
        </FormControl>
      )
    }, [
      showTargets,
      compareTo,
      mode,
      compareToKinds,
      targetsPlus,
      selected,
      deleted,
      config,
      others
    ])
  React.useEffect(() => {
    if (isNil(kinds)) return
    const { kind } = record
    setShowKinds(
      kinds[
        includes('高圧')(kind) ? (includes('低圧')(kind) ? 2 : 0) : 1
      ] as string[]
    )
  }, [record])
  React.useEffect(() => {
    if (isNil(kindArg)) return
    if (tab !== 4) setTab(4)
    if (kindArg === 'monthly' && demandTab !== 1) {
      setDemandTab(1)
      setDateViews(dateViews[1] as DateView[])
    } else if (kindArg === 'daily' && demandTab !== 2) {
      setDemandTab(2)
      setDateViews(dateViews[0] as DateView[])
    }
  }, [kindArg, tab, demandTab])
  // React.useEffect(() => {
  //   if (!isNil(scrollRef.current)) scrollRef.current.scrollIntoView()
  // })
  if (isNil(spin)) return <NotFound />
  if (isNil(date) || isEmpty(date)) return <></>
  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Tabs
          value={tab}
          onChange={handleChange}
          TabIndicatorProps={{ style: { background: 'red' } }}>
          {includes('daily')(tabs) && (
            <Tab
              label={translate('resources.spots.tabs.daily')}
              sx={{ border: 0.6 }}
            />
          )}
          {includes('monthly')(tabs) && (
            <Tab
              label={translate('resources.spots.tabs.monthly')}
              sx={{ border: 0.6 }}
            />
          )}
          {includes('monthly')(tabs) && (
            <Tab
              label={map((line: string) => (
                <Typography variant="inherit" key={line}>
                  {line}
                </Typography>
              ))(
                split('\n')(translate('resources.spots.tabs.monthlyCalendar'))
              )}
              sx={{ border: 0.6 }}
            />
          )}
          {includes('yearly')(tabs) && (
            <Tab
              label={translate('resources.spots.tabs.yearly')}
              sx={{ border: 0.6 }}
            />
          )}
          {kind === '高圧' && (
            <Tab
              label={translate('resources.spots.tabs.demand')}
              sx={{ border: 0.6 }}
            />
          )}
        </Tabs>
        <Grid
          container
          sx={
            window.innerWidth < 1200
              ? { mt: '1em', mb: '1em' }
              : { mt: '1em', mb: '1em' }
          }>
          {/* {tab === 0 && (
            <>
              <Grid size={includes('高圧')(record.kind) ? 6 : 12}>
                <div id="amounts" />
              </Grid>
              {includes('高圧')(record.kind) && (
                <Grid size={6}>
                  <div id="demand" />
                  <div id="demandHint" />
                </Grid>
              )}
            </>
          )} */}
          {(tab === 0 || tab === 1 || tab === 3) && (
            <Grid size={12}>
              <div id="amounts" />
            </Grid>
          )}
        </Grid>
        {tab === 0 && (
          <>
            <Box>
              <ElectricityChartDaily
                spin={spin}
                partner={partner}
                date={date}
                // compareTo={compareTo as ElectricityChartDailyKind}
                compareTo={showCompareTo ?? undefined}
                targets={showTargets ?? undefined}
                others={others ? selected : undefined}
                tempature={tempature[0] as boolean}
                scrollRef={scrollRef}
                configChart={configChart}
                configMappings={configMappings}
                record={record}
                classsification={classification}
                showKinds={showKinds}
                amountsId={
                  !isNil(showAmount) && (showAmount as boolean)
                    ? 'amounts'
                    : undefined
                }
                demandId={
                  !isNil(showDemand) &&
                  (showDemand as boolean) &&
                  includes('高圧')(record.kind) &&
                  (isNil(classification) || classification === '供給')
                    ? 'demand'
                    : undefined
                }
              />
              {spotChartControl(
                'daily',
                configKinds.daily ?? [
                  'lastWeek',
                  'lastYearWithMonthlyAverage',
                  'lastYearWithYearlyAverage'
                ]
              )}
            </Box>
          </>
        )}
        {tab === 1 && (
          <Box>
            {/* <Grid container spacing={2}>
              <Grid size={7}> */}
            <ElectricityChartMonthly
              spin={spin}
              partner={partner}
              date={format('yyyy-MM-dd')(startOfMonth(parseISO(date)))}
              inputDate={date}
              // compareTo={compareTo as ElectricityChartMonthlyKind}
              compareTo={showCompareTo ?? undefined}
              targets={showTargets ?? undefined}
              others={others ? selected : undefined}
              tempature={tempature}
              scrollRef={scrollRef}
              configChart={configChart}
              configMappings={configMappings}
              record={record}
              classification={classification}
              showKinds={showKinds}
              amountsId={
                !isNil(showAmount) && (showAmount as boolean)
                  ? 'amounts'
                  : undefined
              }
              demandId={
                !isNil(showDemand) &&
                (showDemand as boolean) &&
                includes('高圧')(record.kind)
                  ? 'demand'
                  : undefined
              }
            />
            {/* </Grid> */}
            {/* <Grid size={5}>
                <ElectricityChartMonthlyByDays
                  spin={spin}
                  date={format('yyyy-MM-dd')(startOfMonth(parseISO(date)))}
                  configChart={configChart}
                />
              </Grid> */}
            {/* </Grid> */}
            {spotChartControl(
              'monthly',
              configKinds.monthly ?? [
                'lastYear',
                'lastMonth',
                'lastYearWithMonthlyAverage'
              ]
            )}
          </Box>
        )}
        {tab === 2 && (
          <Box>
            <ElectricityMonthlyTable
              date={format('yyyy-MM-dd')(startOfMonth(parseISO(date)))}
              spin={spin}
              name={name}
              kind={kind}
              scrollRef={scrollRef}
            />
            {spotChartControl('none')}
          </Box>
        )}
        {tab === 3 && (
          <Box>
            <ElectricityChartYearly
              spin={spin}
              partner={partner}
              date={
                isBefore(
                  parseISO(
                    `${parseISO(date).getFullYear()}-${
                      startMonthOfFiscalYear as string
                    }-01`
                  )
                )(parseISO(date))
                  ? `${parseISO(date).getFullYear() - 1}-${
                      startMonthOfFiscalYear as string
                    }-01`
                  : `${parseISO(date).getFullYear()}-${
                      startMonthOfFiscalYear as string
                    }-01`
              }
              // compareTo={compareTo as ElectricityChartYearlyKind}]
              compareTo={showCompareTo ?? undefined}
              scrollRef={scrollRef}
              configChart={configChart}
              configMappings={configMappings}
              classification={classification}
              showKinds={showKinds}
              amountsId={
                !isNil(showAmount) && (showAmount as boolean)
                  ? 'amounts'
                  : undefined
              }
            />
            {spotChartControl('yearly', configKinds.yearly ?? ['lastYear'])}
          </Box>
        )}
        {tab === 4 && (
          <>
            <Box
              sx={{
                ml: window.innerWidth >= 900 ? '26em' : '21.5em',
                mt: '-2em'
              }}>
              <Tabs
                value={demandTab}
                onChange={handleDemandChange}
                TabIndicatorProps={{ style: { background: 'red' } }}>
                <Tab
                  label={translate('resources.spots.demandTabs.yearly')}
                  sx={{ border: 0.6 }}
                />
                <Tab
                  label={translate('resources.spots.demandTabs.monthly')}
                  sx={{ border: 0.6 }}
                />
                <Tab
                  label={translate('resources.spots.demandTabs.daily')}
                  sx={{ border: 0.6 }}
                />
                <Tab
                  label={translate('resources.spots.demandTabs.trend')}
                  sx={{ border: 0.6 }}
                />
              </Tabs>
            </Box>
            <Box>
              {demandTab === 0 && (
                <>
                  <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
                    <div id="demand" />
                  </Grid>
                  <Box>
                    <DemandChartYearly
                      spin={spin}
                      partner={partner}
                      scrollRef={scrollRef}
                      date={date}
                      demandId={
                        includes('高圧')(record.kind) &&
                        (isNil(classification) || classification === '供給')
                          ? 'demand'
                          : undefined
                      }
                      configChart={configChart}
                    />
                  </Box>
                </>
              )}
              {demandTab === 1 && (
                <>
                  <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
                    <div id="demand" />
                  </Grid>
                  <Box>
                    <DemandChartMonthly
                      spin={spin}
                      partner={partner}
                      scrollRef={scrollRef}
                      date={date}
                      kind={kindArg}
                      demandId={
                        includes('高圧')(record.kind) &&
                        (isNil(classification) || classification === '供給')
                          ? 'demand'
                          : undefined
                      }
                      configChart={configChart}
                    />
                  </Box>
                </>
              )}
              {demandTab === 2 && (
                <>
                  <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
                    <div id="demand" />
                  </Grid>
                  <Box>
                    <DemandChartDaily
                      spin={spin}
                      partner={partner}
                      scrollRef={scrollRef}
                      date={date}
                      kind={kindArg}
                      demandId={
                        includes('高圧')(record.kind) &&
                        (isNil(classification) || classification === '供給')
                          ? 'demand'
                          : undefined
                      }
                      configChart={configChart}
                    />
                  </Box>
                </>
              )}
              {demandTab === 3 && (
                <>
                  <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
                    <div id="demand" />
                  </Grid>
                  <Box>
                    <DemandChartTrend
                      spin={spin}
                      partner={partner}
                      scrollRef={scrollRef}
                      date={date}
                      demandId={
                        includes('高圧')(record.kind) &&
                        (isNil(classification) || classification === '供給')
                          ? 'demand'
                          : undefined
                      }
                      configChart={configChart}
                    />
                  </Box>
                </>
              )}
              {spotChartControl('none')}
            </Box>
          </>
        )}
      </Grid>
      {/* <Grid size={4}>
        {((!isNil(showAmount) && (showAmount as boolean)) ||
          (!isNil(showDemand) && (showDemand as boolean))) && (
          <Grid container spacing={2}>
            {!isNil(showAmount) && (showAmount as boolean) && (
              <Grid
                item
                xs={
                  !isNil(showDemand) &&
                  (showDemand as boolean) &&
                  /高圧/.test(record['kind'])
                    ? 6
                    : 12
                }>
                <Card>
                  <div id="amounts" />
                </Card>
              </Grid>
            )}
            {!isNil(showDemand) &&
            (showDemand as boolean) &&
            /高圧/.test(record['kind']) ? (
              <Grid
                item
                xs={!isNil(showAmount) && (showAmount as boolean) ? 6 : 12}>
                <Card>
                  <div id="demand" />
                </Card>
              </Grid>
            ) : (
              <></>
            )}
          </Grid>
        )}
        {!isNil(showDemand) &&
          (showDemand as boolean) &&
          /高圧/.test(record['kind']) && (
            <Card>
              <div id="demandHint" />
            </Card>
          )} */}
      {/* <Card
          sx={{
            mt:
              (!isNil(showAmount) && (showAmount as boolean)) ||
              (!isNil(showDemand) && (showDemand as boolean))
                ? '1em'
                : '0em'
          }}>
          <SimpleShowLayout>
            {map((key: string) => {
              if (key === 'spin' && size(split(',')(spin)) > 1) {
                // @ts-expect-error ts(2339)
                return map.convert({ cap: false })(
                  (eachSpin: string, index: number) => {
                    return (
                      <TextField
                        key={`${key}-${index}`}
                        record={{ spin: eachSpin }}
                        source={key}
                        label={
                          index === 0
                            ? config.fields[key].label ??
                              translate(`resources.mergedUsers.fields.${key}`)
                            : false
                        }
                      />
                    )
                  }
                )(split(',')(spin))
                // }
                // if (key === 'kind' && size(split('/')(kind)) > 1) {
                //   const kinds = join('/')(uniq(split('/')(kind)))
                //   // @ts-expect-error ts(2339)
                //   return map.convert({ cap: false })(
                //     (eachKind: string, index: number) => {
                //       return (
                //         <TextField
                //           key={`${key}-${index}`}
                //           record={{ kind: eachKind }}
                //           source={key}
                //           label={
                //             index === 0
                //               ? config.fields[key].label ??
                //                 translate(`resources.mergedUsers.fields.${key}`)
                //               : false
                //           }
                //         />
                //       )
                //     }
                //   )(split('/')(kinds))
              } else {
                return (
                  <TextField
                    key={key}
                    source={key}
                    label={
                      config.fields[key].label ??
                      translate(`resources.mergedUsers.fields.${key}`)
                    }
                  />
                )
              }
            })(compact(keys(config.fields)))}
          </SimpleShowLayout>
        </Card> */}
      {/* </Grid> */}
    </Grid>
  )
}

export const SpotShow2: React.FC<
  {
    target: string
    partner: string
    date: string
    kind: string | null | undefined
    startMonthOfFiscalYear: string
    setDateViews: (value: DateView[]) => void
  } & ScrollProps &
    ConfigProps
> = ({
  target,
  partner,
  scrollRef,
  config,
  date,
  kind,
  startMonthOfFiscalYear,
  setDateViews
}) => {
  const { isLoading } = useGetOne('mappings', { id: target })
  if (isLoading) return null
  return (
    <ShowBase resource="mappings" id={target}>
      <SpotShowContent
        partner={partner}
        date={date}
        config={config}
        kind={kind}
        scrollRef={scrollRef}
        startMonthOfFiscalYear={startMonthOfFiscalYear}
        setDateViews={setDateViews}
      />
    </ShowBase>
  )
}
export const Spot2: React.FC<
  {
    partner: string
    target: string | null | undefined
    date: string | null | undefined
    kind: string | null | undefined
    permissions: string[]
  } & ScrollProps &
    ConfigProps
> = ({ config, partner, scrollRef, target: targetArg, date, kind }) => {
  const { startMonthOfFiscalYear, maxDate, Limits: configLimits } = config
  const translate = useTranslate()
  const navigate = useNavigate()
  const [show, setShow] = React.useState(!isNil(targetArg))
  const [target, setTarget] = React.useState<string | null | undefined>(
    targetArg
  )
  const [targetDate, setTargetDate] = React.useState<string>(
    maxDate ??
      (isNil(process.env['INPUT'])
        ? format('yyyy-MM-dd')(
            isAfter(parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`))(
              Date.now()
            )
              ? subDays(1)(Date.now())
              : subDays(2)(Date.now())
          )
        : '2024-12-12')
  )
  const [dateViews, setDateViews] = React.useState<DateView[]>([
    'year',
    'month',
    'day'
  ])
  const handleRowClick: RowClickFunction = (
    id: Identifier,
    _resource: string,
    _record: RaRecord
  ): any => {
    setTarget(id as string)
    setShow(true)
    if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
      scrollRef.current.scrollIntoView()
    }
    return false
  }
  React.useEffect(() => {
    if (isNil(date)) return
    setTargetDate(date)
  }, [date])
  const { pathname } = window.location
  React.useEffect(() => {
    if (includes('spot/demand')(pathname)) {
      navigate({
        pathname: join('/')(slice(0, 4)(split('/')(pathname)))
      })
    }
  }, [pathname])
  const { start } = configLimits ?? { start: undefined }
  const parts = [
    <SpotSelect
      rowClick={handleRowClick}
      opened={isNil(target)}
      config={config}
      target={target}
      key="spotSelect"
    />,
    <Form
      defaultValues={{
        date: targetDate
      }}
      key="dateInputd">
      <DateInput
        source="date"
        label={translate('resources.spots.fields.date')}
        validate={required()}
        disableFuture
        minDate={!isNil(start) ? parseISO(start) : undefined}
        maxDate={
          !isNil(maxDate)
            ? parseISO(maxDate)
            : isAfter(parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`))(
                Date.now()
              )
            ? subDays(1)(Date.now())
            : subDays(2)(Date.now())
        }
        views={dateViews}
        onChange={(_source: string, value: string): void => {
          setTargetDate(value)
        }}
        withControl
      />
    </Form>
  ]
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 5,
          flexDirection: 'row',
          bgcolor: '#F9DD87',
          p: '0.5em'
        }}>
        {window.innerWidth <= 900 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column'
            }}>
            {parts}
          </Box>
        ) : (
          <>{parts}</>
        )}
      </Box>
      {show && !isNil(target) && (
        <Box>
          <Form>
            <SpotShow2
              target={target}
              partner={partner}
              scrollRef={scrollRef}
              config={config}
              date={date ?? targetDate}
              kind={kind}
              startMonthOfFiscalYear={startMonthOfFiscalYear}
              setDateViews={setDateViews}
            />
          </Form>
        </Box>
      )}
    </>
  )
}
